import { createContext, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";

const ContactContext = createContext(null);
export const useContact = () => useContext(ContactContext);

function ContactModal({ onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success(t("modal.success"));
      onClose();
    } catch {
      toast.error(t("modal.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="modal-backdrop" data-testid="contact-modal" onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="contact-modal" onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 34, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
        <button className="close-btn" onClick={onClose} data-testid="contact-modal-close" aria-label="Close"><X /></button>
        <span className="eyebrow">{t("modal.eyebrow")}</span>
        <h2>{t("modal.title")}</h2>
        <p>{t("modal.sub")}</p>
        <form onSubmit={submit}>
          <input required placeholder={t("modal.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
          <input required type="email" placeholder={t("modal.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
          <input placeholder={t("modal.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
          <textarea required placeholder={t("modal.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
          <button className="btn btn-dark full" type="submit" disabled={busy} data-testid="contact-submit-button">
            {t("modal.submit")} <ArrowUpRight size={16} />
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export function ContactProvider({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <ContactContext.Provider value={{ open: () => setOpen(true) }}>
      {children}
      <AnimatePresence>{open && <ContactModal onClose={() => setOpen(false)} />}</AnimatePresence>
    </ContactContext.Provider>
  );
}
